import React from "react";
import styled from "@emotion/styled";
import { useInView } from "react-intersection-observer";
import { colors } from "style";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";

/**
 * ScrollToTopButton
 * A floating button that appears when page header goes out of view
 */
const ScrollToTopButton = () => {
  const { ref: triggerRef, inView } = useInView({
    threshold: 0,
    rootMargin: "0px 0px -300px 0px", // Show button when trigger is 300px out of view
  });

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Show button when trigger element is NOT in view
  const isVisible = !inView;

  return (
    <>
      {/* Invisible trigger element positioned at the top */}
      <TriggerElement ref={triggerRef} />
      <ScrollButton isVisible={isVisible} onClick={scrollToTop} title="Scroll to top">
        <Icon type={ICON_TYPE.chevronUp} size={ICON_SIZE.large} color={colors.white} />
      </ScrollButton>
    </>
  );
};

const TriggerElement = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
`;

const ScrollButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 4.8rem;
  height: 4.8rem;
  border-radius: 1rem;
  background-color: ${colors.purpleRainBase};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transition: all 0.3s ease;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  visibility: ${(props) => (props.isVisible ? "visible" : "hidden")};
  transform: ${(props) => (props.isVisible ? "translateY(0)" : "translateY(10px)")};

  &:hover {
    background-color: ${colors.purpleRainDark1};
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: 2px solid ${colors.purpleRainLight3};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    bottom: 1.5rem;
    right: 1.5rem;
    width: 4rem;
    height: 4rem;
  }
`;

export default ScrollToTopButton;
